import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  FaBarcode,
  FaTag,
  FaIndustry,
  FaFingerprint,
  FaRegImage,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaChevronLeft,
  FaChevronRight,
  FaHistory,
  FaBolt,
  FaRulerHorizontal,
  FaRulerVertical,
  FaCircleNotch,
  FaWeightHanging,
  FaFlask,
  FaCopy,
  FaCheck,
} from 'react-icons/fa'
import {
  getRepuestosPorCodigoSap,
  getRepuestosPorNpf,
  getImagesByMaterial,
} from 'src/services/axiomaService'
import './AxiomaEscanerContainer.css'

// ---------------------------------------------------------------------------
// Mapeo de campos del material -> etiquetas de solo lectura que se muestran.
// Cada campo intenta las claves en orden y usa la primera con valor.
// Ajustar aquí si el backend cambia los nombres de las propiedades.
// ---------------------------------------------------------------------------
const primerValor = (material, claves) => {
  const clave = claves.find((k) => {
    const valor = material?.[k]
    return valor !== null && valor !== undefined && String(valor).trim() !== ''
  })
  return clave ? material[clave] : null
}

const CAMPOS_MATERIAL = [
  {
    id: 'marca',
    etiqueta: 'Marca',
    icono: FaIndustry,
    claves: [
      'revision_marca_nombre',
      'marca_nombre',
      'repuesto_fabricante',
      'marca',
    ],
  },
  {
    id: 'npf',
    etiqueta: 'NPF',
    icono: FaFingerprint,
    claves: ['repuesto_npf', 'npf'],
  },
  {
    id: 'largo',
    etiqueta: 'Largo mayor',
    icono: FaRulerHorizontal,
    claves: ['revision_largo_mm', 'repuesto_largo_mm', 'largo'],
    sufijo: 'mm',
  },
  {
    id: 'ancho',
    etiqueta: 'Ancho mayor',
    icono: FaRulerHorizontal,
    claves: ['revision_ancho_mm', 'repuesto_ancho_mm', 'ancho'],
    sufijo: 'mm',
  },
  {
    id: 'alto',
    etiqueta: 'Alto mayor',
    icono: FaRulerVertical,
    claves: ['revision_alto_mm', 'repuesto_alto_mm', 'alto'],
    sufijo: 'mm',
  },
  {
    id: 'diametro',
    etiqueta: 'Diámetro mayor',
    icono: FaCircleNotch,
    claves: ['revision_diametro_mm', 'repuesto_diametro_mm'],
    sufijo: 'mm',
  },
  {
    id: 'peso',
    etiqueta: 'Peso (g)',
    icono: FaWeightHanging,
    claves: ['revision_peso_g', 'repuesto_peso_g'],
    sufijo: 'g',
  },
  {
    id: 'composicion',
    etiqueta: 'Composición producto',
    icono: FaFlask,
    claves: ['revision_composicion', 'repuesto_composicion'],
  },
]

const formatearValorCampo = (valor, sufijo) => {
  if (valor === null || valor === undefined || String(valor).trim() === '') {
    return null
  }
  const texto = String(valor).trim()
  if (!sufijo) return texto
  // Evitar duplicar unidad si el backend ya la incluye.
  if (texto.toLowerCase().endsWith(sufijo.toLowerCase())) return texto
  return `${texto} ${sufijo}`
}

// Nombre principal del material (título de la tarjeta).
// Prioriza la descripción correcta que editó el axiomer; SAP solo si no hay.
const obtenerNombre = (material) =>
  primerValor(material, [
    'revision_descripcion_correcta',
    'revision_descripcion_breve',
    'repuesto_descripcion',
    'texto_breve',
  ]) || 'Material sin nombre'

const obtenerSap = (material) =>
  primerValor(material, [
    'repuesto_codigo_material_sap',
    'material',
    'codigo_sap',
  ]) || '—'

const copiarTexto = async (texto) => {
  const valor = String(texto ?? '').trim()
  if (!valor) return false
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(valor)
      return true
    }
    const area = document.createElement('textarea')
    area.value = valor
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.left = '-9999px'
    document.body.appendChild(area)
    area.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(area)
    return ok
  } catch {
    return false
  }
}

const rearmarInputEscaner = () => {
  requestAnimationFrame(() => {
    document.querySelector('.escaner-input')?.focus()
  })
}

const BotonCopiar = ({ texto, etiqueta }) => {
  const [copiado, setCopiado] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const valor = String(texto ?? '').trim()
  if (!valor || valor === '—') return null

  const handleClick = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    const ok = await copiarTexto(valor)
    if (!ok) return
    setCopiado(true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCopiado(false), 1600)
    rearmarInputEscaner()
  }

  return (
    <button
      type="button"
      className={`escaner-copy ${copiado ? 'ok' : ''}`}
      onClick={handleClick}
      title={copiado ? 'Copiado' : `Copiar ${etiqueta}`}
      aria-label={copiado ? 'Copiado' : `Copiar ${etiqueta}`}
    >
      {copiado ? <FaCheck /> : <FaCopy />}
    </button>
  )
}

// Normaliza la respuesta de repuestos por código (varias formas posibles).
const extraerRepuestos = (respuesta) => {
  if (!respuesta) return []
  if (Array.isArray(respuesta.repuestos)) return respuesta.repuestos
  if (Array.isArray(respuesta?.success?.repuestos)) return respuesta.success.repuestos
  if (Array.isArray(respuesta.success)) return respuesta.success
  if (Array.isArray(respuesta)) return respuesta
  return []
}

// ---------------------------------------------------------------------------
// Galería de imágenes del material (con estado de carga propio)
// ---------------------------------------------------------------------------
const GaleriaImagenes = ({ imagenes, cargando }) => {
  const [activa, setActiva] = useState(0)

  useEffect(() => {
    setActiva(0)
  }, [imagenes])

  if (cargando) {
    return (
      <div className="escaner-media">
        <div className="escaner-media-principal escaner-skeleton" />
        <div className="escaner-media-thumbs">
          <div className="escaner-thumb escaner-skeleton" />
          <div className="escaner-thumb escaner-skeleton" />
          <div className="escaner-thumb escaner-skeleton" />
        </div>
      </div>
    )
  }

  if (!imagenes || imagenes.length === 0) {
    return (
      <div className="escaner-media">
        <div className="escaner-media-principal escaner-media-vacia">
          <FaRegImage />
          <span>Sin imágenes</span>
        </div>
      </div>
    )
  }

  const indice = Math.min(activa, imagenes.length - 1)
  const total = imagenes.length

  return (
    <div className="escaner-media">
      <div className="escaner-media-principal">
        <img
          src={imagenes[indice].signed_url}
          alt={imagenes[indice].nombre_objeto || 'Imagen del material'}
        />
        {total > 1 && (
          <>
            <button
              type="button"
              className="escaner-media-nav escaner-media-nav-prev"
              onClick={() => setActiva((prev) => (prev - 1 + total) % total)}
              aria-label="Imagen anterior"
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              className="escaner-media-nav escaner-media-nav-next"
              onClick={() => setActiva((prev) => (prev + 1) % total)}
              aria-label="Imagen siguiente"
            >
              <FaChevronRight />
            </button>
            <span className="escaner-media-contador">
              {indice + 1} / {total}
            </span>
          </>
        )}
      </div>
      {total > 1 && (
        <div className="escaner-media-thumbs">
          {imagenes.map((img, idx) => (
            <button
              type="button"
              key={img.nombre_objeto || img.signed_url || idx}
              className={`escaner-thumb ${idx === indice ? 'active' : ''}`}
              onClick={() => setActiva(idx)}
            >
              <img src={img.signed_url} alt={img.nombre_objeto || `Miniatura ${idx + 1}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tarjeta de solo lectura del material escaneado
// ---------------------------------------------------------------------------
const TarjetaMaterial = ({ material, imagenes, cargandoImagenes }) => {
  const nombre = obtenerNombre(material)
  const sap = obtenerSap(material)

  return (
    <div className="escaner-card" key={sap}>
      <div className="escaner-card-media-col">
        <GaleriaImagenes imagenes={imagenes} cargando={cargandoImagenes} />
      </div>

      <div className="escaner-card-info">
        <div className="escaner-card-head">
          <div className="escaner-card-name">
            <FaTag className="escaner-card-name-icon" />
            <span>{nombre}</span>
            <BotonCopiar texto={nombre} etiqueta="descripción" />
          </div>
          <div className="escaner-card-sap">
            <FaBarcode />
            <span className="escaner-card-sap-label">SAP</span>
            <span className="escaner-card-sap-value">{sap}</span>
            <BotonCopiar texto={sap} etiqueta="código SAP" />
          </div>
        </div>

        <div className="escaner-specs">
          {CAMPOS_MATERIAL.map((campo) => {
            const Icono = campo.icono
            const valor = formatearValorCampo(
              primerValor(material, campo.claves),
              campo.sufijo,
            )
            return (
              <div
                key={campo.id}
                className={`escaner-spec ${campo.ancho === 'full' ? 'escaner-spec-full' : ''}`}
              >
                <div className="escaner-spec-icon">
                  <Icono />
                </div>
                <div className="escaner-spec-body">
                  <span className="escaner-spec-label">{campo.etiqueta}</span>
                  <span
                    className={`escaner-spec-value ${valor ? '' : 'escaner-spec-value-empty'}`}
                  >
                    {valor || 'Sin información'}
                  </span>
                </div>
                {valor && (
                  <BotonCopiar texto={valor} etiqueta={campo.etiqueta} />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Contenedor principal del escáner
// ---------------------------------------------------------------------------
const AxiomaEscanerContainer = () => {
  const [codigo, setCodigo] = useState('')
  const [estado, setEstado] = useState('idle') // idle | loading | found | notfound | error
  const [material, setMaterial] = useState(null)
  const [imagenes, setImagenes] = useState([])
  const [cargandoImagenes, setCargandoImagenes] = useState(false)
  const [inputEnfocado, setInputEnfocado] = useState(false)
  const [ultimoCodigo, setUltimoCodigo] = useState('')
  const [historial, setHistorial] = useState([])
  const [totalEscaneos, setTotalEscaneos] = useState(0)

  const inputRef = useRef(null)
  const containerRef = useRef(null)
  const seqRef = useRef(0)

  const enfocarInput = useCallback(() => {
    // Pequeño defer para asegurar el foco tras re-render
    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }, [])

  useEffect(() => {
    enfocarInput()
  }, [enfocarInput])

  // Mantener el input "armado": al hacer clic en zonas vacías del contenedor
  // (no en botones, enlaces, imágenes u otros campos) se re-enfoca el input.
  useEffect(() => {
    const nodo = containerRef.current
    if (!nodo) return undefined
    const handleMouseDown = (e) => {
      if (
        e.target.closest('button') ||
        e.target.closest('a') ||
        e.target.closest('img') ||
        e.target.closest('input') ||
        e.target.closest('.escaner-card-info')
      ) {
        return
      }
      enfocarInput()
    }
    nodo.addEventListener('mousedown', handleMouseDown)
    return () => nodo.removeEventListener('mousedown', handleMouseDown)
  }, [enfocarInput])

  const registrarHistorial = useCallback((cod, resultado, nombre) => {
    setHistorial((prev) =>
      [
        {
          codigo: cod,
          resultado, // 'found' | 'notfound' | 'error'
          nombre: nombre || null,
          hora: new Date().toLocaleTimeString('es-CO', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
        },
        ...prev.filter((h) => h.codigo !== cod),
      ].slice(0, 8),
    )
  }, [])

  const consultar = useCallback(
    async (codigoRaw) => {
      const cod = String(codigoRaw ?? '').trim()
      if (!cod) {
        enfocarInput()
        return
      }

      seqRef.current += 1
      const seq = seqRef.current
      setEstado('loading')
      setUltimoCodigo(cod)
      setMaterial(null)
      setImagenes([])
      setCodigo('')

      try {
        // 1) Buscar primero por código SAP (código de material).
        const respuesta = await getRepuestosPorCodigoSap(cod)
        if (seq !== seqRef.current) return // llegó una consulta más nueva

        let repuestos = extraerRepuestos(respuesta)

        // 2) Si no hay por SAP, buscar por NPF. Ese endpoint trae el SAP
        // en "material"; con el primero se vuelve a consultar por código.
        if (!repuestos.length) {
          const respuestaNpf = await getRepuestosPorNpf(cod)
          if (seq !== seqRef.current) return
          const porNpf = extraerRepuestos(respuestaNpf)
          const sapDesdeNpf = primerValor(porNpf[0], [
            'material',
            'repuesto_codigo_material_sap',
            'codigo_sap',
          ])
          if (sapDesdeNpf) {
            const respuestaSap = await getRepuestosPorCodigoSap(
              String(sapDesdeNpf).trim(),
            )
            if (seq !== seqRef.current) return
            repuestos = extraerRepuestos(respuestaSap)
          }
        }

        // 3) Si tampoco hay por NPF, entonces sí es "sin resultados".
        if (!repuestos.length) {
          setEstado('notfound')
          setTotalEscaneos((n) => n + 1)
          registrarHistorial(cod, 'notfound')
          enfocarInput()
          return
        }

        const mat = repuestos[0]
        setMaterial(mat)
        setEstado('found')
        setTotalEscaneos((n) => n + 1)
        registrarHistorial(cod, 'found', obtenerNombre(mat))
        enfocarInput()

        // Imágenes: consulta independiente con su propio loader.
        const sapMaterial = obtenerSap(mat)
        setCargandoImagenes(true)
        try {
          const respImg = await getImagesByMaterial(sapMaterial)
          if (seq !== seqRef.current) return
          const imgs = (respImg?.success || []).filter((i) => i?.signed_url)
          // La imagen favorita (si existe) va primero.
          const favorita = mat?.revision_imagen_favorita
          const ordenadas = favorita
            ? [
                ...imgs.filter((i) => i.nombre_objeto === favorita),
                ...imgs.filter((i) => i.nombre_objeto !== favorita),
              ]
            : imgs
          setImagenes(ordenadas)
        } finally {
          if (seq === seqRef.current) setCargandoImagenes(false)
        }
      } catch (error) {
        if (seq !== seqRef.current) return
        console.error('Error al consultar el material:', error)
        setEstado('error')
        registrarHistorial(cod, 'error')
        enfocarInput()
      }
    },
    [enfocarInput, registrarHistorial],
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    if (estado === 'loading') return
    consultar(codigo)
  }

  const cargando = estado === 'loading'
  const hayResultado = !cargando && estado === 'found' && Boolean(material)

  return (
    <div className="escaner-container" ref={containerRef}>
      <div className="escaner-top">
        <h1 className="escaner-title">
          <FaBarcode className="escaner-title-icon" />
          Escáner
        </h1>
        {totalEscaneos > 0 && (
          <div className="escaner-header-stat">
            <span className="escaner-header-stat-num">{totalEscaneos}</span>
            <span className="escaner-header-stat-label">escaneos</span>
          </div>
        )}
        <form className="escaner-scanbar" onSubmit={handleSubmit}>
          <div
            className={`escaner-input-wrap ${inputEnfocado ? 'focused' : ''} ${
              cargando ? 'loading' : ''
            }`}
          >
            <FaBarcode className="escaner-input-icon" />
            <input
              ref={inputRef}
              type="text"
              className="escaner-input"
              placeholder="Escanee o digite el código y Enter…"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              onFocus={() => setInputEnfocado(true)}
              onBlur={() => setInputEnfocado(false)}
              autoComplete="off"
              spellCheck={false}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              disabled={cargando}
            />
            <span
              className={`escaner-armado ${
                cargando ? 'busy' : inputEnfocado ? 'ready' : 'idle'
              }`}
              title={
                cargando
                  ? 'Consultando…'
                  : inputEnfocado
                  ? 'Listo para escanear'
                  : 'Haga clic para activar'
              }
            >
              <span className="escaner-armado-dot" />
              {cargando ? 'Consultando…' : inputEnfocado ? 'Listo' : 'Inactivo'}
            </span>
          </div>
          <button
            type="submit"
            className="escaner-btn-consultar"
            disabled={cargando || !codigo.trim()}
          >
            <FaBolt />
            Consultar
          </button>
        </form>
      </div>

      <div className={`escaner-layout ${hayResultado || cargando ? 'has-result' : ''}`}>
        <main className="escaner-stage">
          {cargando && (
            <div className="escaner-card escaner-card-loading">
              <div className="escaner-card-media-col">
                <div className="escaner-media">
                  <div className="escaner-media-principal escaner-skeleton" />
                  <div className="escaner-media-thumbs">
                    <div className="escaner-thumb escaner-skeleton" />
                    <div className="escaner-thumb escaner-skeleton" />
                    <div className="escaner-thumb escaner-skeleton" />
                  </div>
                </div>
              </div>
              <div className="escaner-card-info">
                <div className="escaner-skeleton escaner-skeleton-line lg" />
                <div className="escaner-skeleton escaner-skeleton-line sm" />
                <div className="escaner-specs">
                  <div className="escaner-skeleton escaner-skeleton-box" />
                  <div className="escaner-skeleton escaner-skeleton-box" />
                  <div className="escaner-skeleton escaner-skeleton-box" />
                  <div className="escaner-skeleton escaner-skeleton-box" />
                  <div className="escaner-skeleton escaner-skeleton-box" />
                  <div className="escaner-skeleton escaner-skeleton-box" />
                  <div className="escaner-skeleton escaner-skeleton-box" />
                  <div className="escaner-skeleton escaner-skeleton-box" />
                </div>
              </div>
            </div>
          )}

          {hayResultado && (
            <TarjetaMaterial
              material={material}
              imagenes={imagenes}
              cargandoImagenes={cargandoImagenes}
            />
          )}

          {!cargando && estado === 'notfound' && (
            <div className="escaner-placeholder escaner-placeholder-warn">
              <FaExclamationTriangle className="escaner-placeholder-icon" />
              <h2>Material no encontrado</h2>
              <p>
                No hay resultados para el código{' '}
                <strong>{ultimoCodigo}</strong>. Verifique e intente de nuevo.
              </p>
            </div>
          )}

          {!cargando && estado === 'error' && (
            <div className="escaner-placeholder escaner-placeholder-error">
              <FaTimesCircle className="escaner-placeholder-icon" />
              <h2>Ocurrió un error</h2>
              <p>
                No se pudo consultar el código <strong>{ultimoCodigo}</strong>.
                Reintente el escaneo.
              </p>
            </div>
          )}

          {!cargando && estado === 'idle' && (
            <div className="escaner-placeholder">
              <div className="escaner-placeholder-scan">
                <FaBarcode />
              </div>
              <h2>Listo para escanear</h2>
              <p>
                Apunte la pistola al código de barras del material. La ficha
                aparecerá aquí automáticamente.
              </p>
            </div>
          )}
        </main>

        {!hayResultado && !cargando && (
          <aside className="escaner-historial">
            <div className="escaner-historial-head">
              <FaHistory />
              <span>Escaneos recientes</span>
            </div>
            {historial.length === 0 ? (
              <div className="escaner-historial-vacio">
                Aún no hay escaneos en esta sesión.
              </div>
            ) : (
              <ul className="escaner-historial-lista">
                {historial.map((item) => (
                  <li
                    key={`${item.codigo}-${item.hora}`}
                    className={`escaner-historial-item ${item.resultado}`}
                  >
                    <button
                      type="button"
                      className="escaner-historial-btn"
                      onClick={() => consultar(item.codigo)}
                      disabled={cargando}
                      title="Volver a consultar"
                    >
                      <span className="escaner-historial-estado">
                        {item.resultado === 'found' ? (
                          <FaCheckCircle />
                        ) : item.resultado === 'notfound' ? (
                          <FaExclamationTriangle />
                        ) : (
                          <FaTimesCircle />
                        )}
                      </span>
                      <span className="escaner-historial-info">
                        <span className="escaner-historial-codigo">
                          {item.codigo}
                        </span>
                        <span className="escaner-historial-nombre">
                          {item.nombre ||
                            (item.resultado === 'notfound'
                              ? 'No encontrado'
                              : 'Error')}
                        </span>
                      </span>
                      <span className="escaner-historial-hora">{item.hora}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        )}
      </div>
    </div>
  )
}

export default AxiomaEscanerContainer
